import * as z from 'zod';

import type {
  FormTemplateDocument,
  ImageCaptureField,
  LocationPickerField,
  WidgetField,
} from '../model/form-template-document.types';
import { imageCaptureFields } from '../model/hero-image-type';
import { getBindingValue, setBindingValue } from './submission-bindings';
import type { SubmissionBindings } from './submission-bindings';

/**
 * Readers for the values widgets store in their bound slots. A submission blob
 * is persisted free-form and outlives the template that wrote it, so every
 * reader parses rather than casts: a value whose shape no longer matches the
 * widget's schema reads as absent and the rest of the form still renders.
 */

/** The pin a 2d picker stores: a fraction of the surface on each axis. */
export const marker2dCoordinateSchema = z.strictObject({
  x: z.number(),
  y: z.number(),
});
export type Marker2dCoordinate = z.infer<typeof marker2dCoordinateSchema>;

const vector3Schema = z.strictObject({
  x: z.number(),
  y: z.number(),
  z: z.number(),
});

/**
 * The pin a 3d picker stores: the tapped point and the surface normal there,
 * which the server has no mesh to derive. Structurally `ModelViewerLocation`.
 */
export const marker3dCoordinateSchema = z.strictObject({
  position: vector3Schema,
  normal: vector3Schema,
});
export type Marker3dCoordinate = z.infer<typeof marker3dCoordinateSchema>;

/** A pin of either dimensionality; disjoint, so a partial 3d pin is malformed. */
export const markerCoordinateSchema = z.union([
  marker2dCoordinateSchema,
  marker3dCoordinateSchema,
]);
export type MarkerCoordinate = z.infer<typeof markerCoordinateSchema>;

/** Narrows a pin to a flat one with the submission schema's own parse. */
export function isMarker2dCoordinate(
  coordinate: MarkerCoordinate | undefined,
): coordinate is Marker2dCoordinate {
  return marker2dCoordinateSchema.safeParse(coordinate).success;
}

/** One entry of an image-capture slot's manifest. */
export const capturedImageSchema = z.strictObject({
  imageId: z.string(),
  type: z.string(),
});
export type CapturedImage = z.infer<typeof capturedImageSchema>;

/** The raw value bound to one of a widget's slots, or `undefined` if unbound. */
export function readSlotValue(
  field: WidgetField,
  bindings: SubmissionBindings,
  slot: string,
): unknown {
  const path = field.bind?.[slot];
  return path === undefined ? undefined : getBindingValue(bindings, path);
}

/** Where a location surface stores its pin, or `undefined` when unbound. */
export function markerCoordinatePath(
  field: LocationPickerField,
): string | undefined {
  return field.bind?.['coordinate'];
}

/** The pin placed on a location surface, or `undefined` when never placed. */
export function readMarkerCoordinate(
  field: LocationPickerField,
  bindings: SubmissionBindings,
): MarkerCoordinate | undefined {
  const parsed = markerCoordinateSchema.safeParse(
    readSlotValue(field, bindings, 'coordinate'),
  );
  return parsed.success ? parsed.data : undefined;
}

/** The manifest entries captured into an image slot, malformed entries dropped. */
export function readCapturedImages(
  field: ImageCaptureField,
  bindings: SubmissionBindings,
): CapturedImage[] {
  const value = readSlotValue(field, bindings, 'images');
  if (!Array.isArray(value)) {
    return [];
  }
  return value.flatMap((entry) => {
    const parsed = capturedImageSchema.safeParse(entry);
    return parsed.success ? [parsed.data] : [];
  });
}

/** The blob with the photos in `imageIds` dropped from every image slot; unchanged blobs are returned as is. */
export function withoutCapturedImages(
  document: FormTemplateDocument,
  bindings: SubmissionBindings,
  imageIds: ReadonlySet<string>,
): SubmissionBindings {
  let result = bindings;
  for (const field of imageCaptureFields(document)) {
    const path = field.bind?.['images'];
    if (!path) continue;
    const value = getBindingValue(result, path);
    if (!Array.isArray(value)) continue;
    const kept = value.filter(
      (entry) =>
        !imageIds.has(
          (entry as { imageId?: unknown } | null)?.imageId as string,
        ),
    );
    if (kept.length !== value.length)
      result = setBindingValue(result, path, kept);
  }
  return result;
}
